import Phaser from 'phaser';
import { MenuNavigation } from '@/systems/MenuNavigation';
import { buildMainMenuItems } from '@/data/menuConfig';
import { GameState, FINAL_STAGE } from '@/state/GameState';
import { getSaveManager, ACTIVE_SLOT } from '@/systems/SaveManager';
import { formatSurvivalBest } from '@/systems/SurvivalScoring';
import { MusicDirectorAdapter } from '@/systems/MusicDirectorAdapter';
import { createPhaserMusicFactory } from '@/systems/createPhaserMusicFactory';

// ─── Procedural skyline data (deterministic, no assets) ──────────────────────

// 14 building silhouettes spanning 1280px. Two copies scroll to create a loop.
const BUILDINGS: ReadonlyArray<{ x: number; w: number; h: number }> = [
  { x: 0, w: 90, h: 180 },
  { x: 100, w: 70, h: 130 },
  { x: 175, w: 110, h: 240 },
  { x: 290, w: 55, h: 160 },
  { x: 350, w: 140, h: 300 },
  { x: 495, w: 65, h: 140 },
  { x: 565, w: 85, h: 200 },
  { x: 655, w: 120, h: 260 },
  { x: 780, w: 50, h: 110 },
  { x: 835, w: 95, h: 190 },
  { x: 935, w: 130, h: 280 },
  { x: 1070, w: 60, h: 150 },
  { x: 1135, w: 90, h: 220 },
  { x: 1230, w: 50, h: 130 },
];

// Window-light clusters. bx/by are pixel offsets from the building's top-left.
const WINDOWS: ReadonlyArray<{
  buildingIndex: number;
  bx: number;
  by: number;
  rows: number;
  cols: number;
}> = [
  { buildingIndex: 0, bx: 10, by: 20, rows: 5, cols: 3 },
  { buildingIndex: 2, bx: 12, by: 30, rows: 8, cols: 4 },
  { buildingIndex: 4, bx: 15, by: 50, rows: 10, cols: 5 },
  { buildingIndex: 6, bx: 10, by: 30, rows: 6, cols: 3 },
  { buildingIndex: 7, bx: 12, by: 40, rows: 8, cols: 4 },
  { buildingIndex: 9, bx: 8, by: 20, rows: 6, cols: 3 },
  { buildingIndex: 10, bx: 12, by: 45, rows: 9, cols: 5 },
  { buildingIndex: 12, bx: 10, by: 35, rows: 7, cols: 3 },
];

const WIN_GAP = 12; // pixels between window cells
const GROUND_OFFSET = 40; // pixels above canvas bottom for ground level
const SCROLL_PX_PER_FRAME = 0.3;

// ─── Fonts / colours ─────────────────────────────────────────────────────────

const FONT_MONO = '"Courier New", monospace';
const FONT_IMPACT = 'Impact, "Arial Black", sans-serif';

// ─── Scene state ─────────────────────────────────────────────────────────────

type TitleState = 'attract' | 'menu';

// ─── Scene ───────────────────────────────────────────────────────────────────

/**
 * TitleScene — MY-1452.
 *
 * Two-phase flow:
 *   attract → pulsing "PRESS START" over looping noir skyline.
 *   menu    → navigable list (Start / Continue / Co-op / Options / Credits).
 *
 * Any nav/confirm key in attract mode transitions to menu. The key that
 * triggers the transition does NOT simultaneously activate the first item
 * (nav keys move/confirm only once already in menu state).
 */
export class TitleScene extends Phaser.Scene {
  private titleState: TitleState = 'attract';
  private nav!: MenuNavigation;
  private menuTexts: Phaser.GameObjects.Text[] = [];
  private promptText!: Phaser.GameObjects.Text;
  private cursorText!: Phaser.GameObjects.Text;
  private skylineContainer!: Phaser.GameObjects.Container;
  private promptTween!: Phaser.Tweens.Tween;
  private music!: MusicDirectorAdapter;

  constructor() {
    super({ key: 'TitleScene' });
  }

  create(): void {
    const { width, height } = this.scale;

    this.buildBackground(width, height);
    this.buildWordmark(width, height);
    this.buildPrompt(width, height);
    this.buildMenu(width);
    this.bindKeys();

    this.music = new MusicDirectorAdapter(undefined, createPhaserMusicFactory(this));
    this.music.onSceneChange('menu');
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.music.stopAll());
  }

  update(_time: number, delta: number): void {
    // Seamless horizontal loop: two copies of the skyline, each 1280px wide.
    this.skylineContainer.x -= SCROLL_PX_PER_FRAME;
    if (this.skylineContainer.x <= -1280) {
      this.skylineContainer.x = 0;
    }
    this.music.update(delta);
  }

  shutdown(): void {
    this.music.stopAll();
  }

  // ─── Background ────────────────────────────────────────────────────────────

  private buildBackground(width: number, height: number): void {
    const groundY = height - GROUND_OFFSET;

    // Dark sky — two overlapping rectangles approximate a top-to-bottom gradient.
    this.add.rectangle(width / 2, height * 0.15, width, height * 0.3, 0x080c12);
    this.add.rectangle(width / 2, height * 0.5, width, height * 0.7, 0x0d1520);

    // Amber street-glow fog at ground level.
    this.add.rectangle(width / 2, groundY + 20, width, 60, 0xff6b00).setAlpha(0.08);

    // Container holds two skyline copies; scrolling the container creates the loop.
    this.skylineContainer = this.add.container(0, 0);
    const left = this.makeSkylineSlice(groundY);
    const right = this.makeSkylineSlice(groundY);
    right.setX(1280);
    this.skylineContainer.add([left, right]);
  }

  private makeSkylineSlice(groundY: number): Phaser.GameObjects.Graphics {
    const g = this.add.graphics();

    // Building silhouettes.
    g.fillStyle(0x0d1520, 1);
    for (const b of BUILDINGS) {
      g.fillRect(b.x, groundY - b.h, b.w, b.h);
    }

    // Window lights — deterministic amber dots, some dimmed for variety.
    for (const w of WINDOWS) {
      const b = BUILDINGS[w.buildingIndex]!;
      for (let row = 0; row < w.rows; row++) {
        for (let col = 0; col < w.cols; col++) {
          const lit = (row * 3 + col * 2) % 5 !== 0;
          g.fillStyle(lit ? 0xffba08 : 0x2a2010, lit ? 0.85 : 0.35);
          g.fillRect(b.x + w.bx + col * WIN_GAP, groundY - b.h + w.by + row * WIN_GAP, 5, 3);
        }
      }
    }

    // Ground surface.
    g.fillStyle(0x0a0e14, 1);
    g.fillRect(0, groundY, 1280, GROUND_OFFSET);

    return g;
  }

  // ─── Wordmark ──────────────────────────────────────────────────────────────

  private buildWordmark(width: number, height: number): void {
    this.add
      .text(width / 2, height * 0.27, 'MANOR', {
        fontFamily: FONT_IMPACT,
        fontSize: '144px',
        color: '#ef233c',
        stroke: '#080c12',
        strokeThickness: 10,
      })
      .setOrigin(0.5)
      .setShadow(0, 6, 'rgba(239,35,60,0.35)', 24, true, true);

    this.add
      .text(width / 2, height * 0.27 + 96, 'NOIR-PUNK · LONDON · BEAT-EM-UP', {
        fontFamily: FONT_MONO,
        fontSize: '18px',
        color: '#ffba08',
        letterSpacing: 3,
      })
      .setOrigin(0.5);
  }

  // ─── Attract prompt ────────────────────────────────────────────────────────

  private buildPrompt(width: number, height: number): void {
    this.promptText = this.add
      .text(width / 2, height * 0.57, 'PRESS START', {
        fontFamily: FONT_MONO,
        fontSize: '26px',
        color: '#ffffff',
        letterSpacing: 6,
      })
      .setOrigin(0.5);

    this.promptTween = this.tweens.add({
      targets: this.promptText,
      alpha: { from: 1, to: 0.12 },
      duration: 750,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  // ─── Menu ──────────────────────────────────────────────────────────────────

  private buildMenu(width: number): void {
    const save = getSaveManager().load(ACTIVE_SLOT);
    const hasSave =
      (save !== null && save.highestStageCleared > 0) ||
      (!GameState.isGameOver && GameState.current.heroId !== null);
    this.nav = new MenuNavigation(buildMainMenuItems(hasSave));

    const menuTop = 430;
    const menuStep = 52;

    this.menuTexts = this.nav.items.map((item, i) =>
      this.add
        .text(width / 2, menuTop + i * menuStep, item.label, {
          fontFamily: FONT_MONO,
          fontSize: '28px',
          color: item.enabled ? '#ffffff' : '#383d44',
          letterSpacing: 4,
        })
        .setOrigin(0.5)
        .setVisible(false),
    );

    this.cursorText = this.add
      .text(0, 0, '▶', {
        fontFamily: FONT_MONO,
        fontSize: '20px',
        color: '#ef233c',
      })
      .setOrigin(1, 0.5)
      .setVisible(false);

    this.add
      .text(width - 12, this.scale.height - 12, 'v0.0.1 · MY-1452', {
        fontFamily: FONT_MONO,
        fontSize: '11px',
        color: '#3a3f4a',
      })
      .setOrigin(1, 1);

    // Survival "best to gloat" — visible before play, only when a record exists.
    const best = formatSurvivalBest(save?.survivalRecord);
    if (best) {
      this.add
        .text(width / 2, menuTop + this.nav.items.length * menuStep + 24, best, {
          fontFamily: FONT_MONO,
          fontSize: '15px',
          color: '#f6c945',
          letterSpacing: 2,
        })
        .setOrigin(0.5);
    }
  }

  // ─── Input bindings ────────────────────────────────────────────────────────

  private bindKeys(): void {
    const kb = this.input.keyboard!;
    const up = kb.addKey(Phaser.Input.Keyboard.KeyCodes.UP);
    const down = kb.addKey(Phaser.Input.Keyboard.KeyCodes.DOWN);
    const enter = kb.addKey(Phaser.Input.Keyboard.KeyCodes.ENTER);
    const space = kb.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);

    up.on('down', () => this.onUp());
    down.on('down', () => this.onDown());
    enter.on('down', () => this.onConfirm());
    space.on('down', () => this.onConfirm());
  }

  // ─── State transitions ─────────────────────────────────────────────────────

  private toMenu(): void {
    if (this.titleState === 'menu') return;
    this.titleState = 'menu';
    this.promptTween.pause();
    this.promptText.setVisible(false);
    this.menuTexts.forEach((t) => t.setVisible(true));
    this.cursorText.setVisible(true);
    this.syncHighlight();
  }

  private syncHighlight(): void {
    const idx = this.nav.index;

    this.menuTexts.forEach((t, i) => {
      const item = this.nav.items[i];
      if (i === idx) {
        t.setColor('#ffffff').setScale(1.06);
      } else {
        t.setColor(item.enabled ? '#888888' : '#383d44').setScale(1);
      }
    });

    // Cursor sits just left of the selected item's left edge.
    const sel = this.menuTexts[idx];
    this.cursorText.setPosition(sel.x - sel.displayWidth / 2 - 16, sel.y);
  }

  // ─── Key handlers ──────────────────────────────────────────────────────────

  private onUp(): void {
    if (this.titleState === 'attract') {
      this.toMenu();
      return;
    }
    this.nav.moveUp();
    this.syncHighlight();
  }

  private onDown(): void {
    if (this.titleState === 'attract') {
      this.toMenu();
      return;
    }
    this.nav.moveDown();
    this.syncHighlight();
  }

  private onConfirm(): void {
    if (this.titleState === 'attract') {
      this.toMenu();
      return;
    }
    switch (this.nav.confirm()) {
      case 'start':
        GameState.newRun();
        this.scene.start('CharSelectScene');
        break;
      case 'survival':
        GameState.newRun();
        // CharSelect re-applies the mode after its own newRun (via init data).
        this.scene.start('CharSelectScene', { gameMode: 'survival' });
        break;
      case 'boss-rush':
        GameState.newRun();
        this.scene.start('CharSelectScene', { gameMode: 'boss-rush' });
        break;
      case 'score-attack':
        GameState.newRun();
        this.scene.start('CharSelectScene', { gameMode: 'score-attack' });
        break;
      case 'continue': {
        const save = getSaveManager().load(ACTIVE_SLOT);
        const resumeStage =
          save && save.highestStageCleared > 0
            ? Math.min(save.highestStageCleared + 1, FINAL_STAGE)
            : 1;
        this.scene.start('CharSelectScene', { resumeStage });
        break;
      }
      case 'coop':
        // enableCoop() here would be wiped by CharSelect's own newRun(); pass the
        // flag through scene data so CharSelect re-applies it after the reset.
        this.scene.start('CharSelectScene', { coop: true });
        break;
      case 'options':
        this.scene.start('OptionsScene', { returnScene: 'TitleScene' });
        break;
      case 'gallery':
        this.scene.start('EndingsGalleryScene', { slotIndex: ACTIVE_SLOT });
        break;
      case 'credits':
        this.scene.start('CreditsScene');
        break;
    }
  }
}
